// Email delivery settings forms (PB-13-04; email stream PD-580 – PD-587; owner decision B-02): the
// installation sender in the system console and an organisation's own sender in Communications.
// The password field is write-only: the form only says whether one is stored and when it changed.
// The UI hides nothing for security; the API enforces who may read and change these settings.
import {
  MAIL_PROVIDER_VALUES,
  SMTP_SECURITY_VALUES,
  type EmailSenderDto,
  type EmailTestResult,
  type EmailTestStateDto,
  type MailProviderDto,
  type OrganisationEmailSettingsResponse,
  type PasswordStateDto,
  type SmtpConnectionDto,
  type SmtpSecurityDto,
} from '@passage/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { ApiRequestError, api, get } from '../api.ts';
import { useI18n } from '../i18n.tsx';
import { ErrorState, ErrorSummary, Field, Loading } from './ui.tsx';

type PasswordAction = 'keep' | 'set' | 'clear' | 'environment';

export interface SenderFormValues {
  readonly provider?: MailProviderDto;
  readonly enabled?: boolean;
  readonly sendingDomain?: string | null;
  readonly sender: EmailSenderDto;
  readonly smtp: SmtpConnectionDto;
  readonly password: PasswordStateDto;
  readonly version: number;
  readonly updatedAt: string;
  readonly problems: readonly string[];
}

const FIELD_IDS: Record<string, string> = {
  provider: 'eml-provider', sendingDomain: 'eml-domain', fromAddress: 'eml-from', fromNameEn: 'eml-name-en', fromNameAr: 'eml-name-ar', replyTo: 'eml-reply',
  smtpHost: 'eml-host', smtpPort: 'eml-port', smtpSecurity: 'eml-security', smtpUsername: 'eml-user', password: 'eml-password-action', 'password.value': 'eml-password', 'password.name': 'eml-env',
};

/** The sender and SMTP connection form. `scope` decides which fields exist (PD-581, PD-583). */
export function SenderForm({ scope, initial, save, onSaved }: { scope: 'installation' | 'organisation'; initial: SenderFormValues; save: (body: Record<string, unknown>) => Promise<unknown>; onSaved: () => void }) {
  const { t, tk, dateTime } = useI18n();
  const [provider, setProvider] = useState<MailProviderDto>(initial.provider ?? 'smtp');
  const [enabled, setEnabled] = useState(initial.enabled ?? false);
  const [domain, setDomain] = useState(initial.sendingDomain ?? '');
  const [v, setV] = useState({
    fromAddress: initial.sender.fromAddress ?? '', fromNameEn: initial.sender.fromNameEn ?? '', fromNameAr: initial.sender.fromNameAr ?? '', replyTo: initial.sender.replyTo ?? '',
    smtpHost: initial.smtp.host ?? '', smtpPort: initial.smtp.port ? String(initial.smtp.port) : '', smtpUsername: initial.smtp.username ?? '',
  });
  const [security, setSecurity] = useState<SmtpSecurityDto | ''>(initial.smtp.security ?? '');
  const [pwAction, setPwAction] = useState<PasswordAction>('keep');
  const [pw, setPw] = useState('');
  const [envName, setEnvName] = useState(initial.password.environmentName ?? 'PASSAGE_SMTP_PASSWORD');
  const [saved, setSaved] = useState(false);
  const m = useMutation({
    mutationFn: () =>
      save({
        expectedVersion: initial.version,
        ...(scope === 'installation' ? { provider } : { enabled, sendingDomain: domain.trim() || null }),
        fromAddress: v.fromAddress, fromNameEn: v.fromNameEn, fromNameAr: v.fromNameAr, replyTo: v.replyTo,
        smtpHost: v.smtpHost, smtpPort: v.smtpPort ? Number(v.smtpPort) : null, smtpSecurity: security || null, smtpUsername: v.smtpUsername,
        password: pwAction === 'set' ? { kind: 'set', value: pw } : pwAction === 'environment' ? { kind: 'environment', name: envName } : { kind: pwAction },
      }),
    onSuccess: () => {
      setPw('');
      setPwAction('keep');
      setSaved(true);
      onSaved();
    },
  });
  const fe = m.error instanceof ApiRequestError ? m.error.fields : {};
  const showSmtp = scope === 'organisation' || provider === 'smtp';
  const text = (k: keyof typeof v, label: string, opts: { hint?: string; type?: string; ltr?: boolean; full?: boolean } = {}) => (
    <Field id={FIELD_IDS[k]} label={label} {...(opts.hint ? { hint: opts.hint } : {})} error={fe[k]} {...(opts.full ? { full: true } : {})}>
      {(p) => <input {...p} type={opts.type ?? 'text'} {...(opts.ltr ? { dir: 'ltr' } : {})} autoComplete="off" value={v[k]} onChange={(e) => (setSaved(false), setV((x) => ({ ...x, [k]: e.target.value })))} />}
    </Field>
  );
  const pwState = initial.password;
  return (
    <form
      noValidate
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        m.mutate();
      }}
    >
      <ErrorSummary error={m.error} fieldIds={FIELD_IDS} />
      {saved && !m.isPending ? <p className="note" role="status">{t('eml.saved')}</p> : null}
      {initial.problems.length ? (
        <div className="note warning">
          <strong>{t('eml.problems.title')}</strong>
          <ul className="problems">
            {initial.problems.map((p) => (
              <li key={p}>{tk(`eml.problem.${p}`, undefined, p)}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="formgrid">
        {scope === 'installation' ? (
          <Field id={FIELD_IDS.provider} label={t('eml.provider')} hint={tk(`eml.provider.${provider}.hint`)} error={fe.provider} full>
            {(p) => (
              <select {...p} value={provider} onChange={(e) => setProvider(e.target.value as MailProviderDto)}>
                {MAIL_PROVIDER_VALUES.map((x) => (
                  <option key={x} value={x}>
                    {tk(`eml.provider.${x}`)}
                  </option>
                ))}
              </select>
            )}
          </Field>
        ) : (
          <>
            <div className="field full">
              <label>
                <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> {t('eml.org.enabled')}
              </label>
              <small>{t('eml.org.enabled.hint')}</small>
            </div>
            <Field id={FIELD_IDS.sendingDomain} label={t('eml.org.sendingDomain')} hint={t('eml.org.sendingDomain.hint')} error={fe.sendingDomain} full>
              {(p) => <input {...p} dir="ltr" autoComplete="off" value={domain} onChange={(e) => setDomain(e.target.value)} />}
            </Field>
          </>
        )}
      </div>
      <fieldset className="subsection">
        <legend>{t('eml.section.sender')}</legend>
        <div className="formgrid">
          {text('fromAddress', t('eml.fromAddress'), { hint: t('eml.fromAddress.hint'), type: 'email', ltr: true })}
          {text('replyTo', t('eml.replyTo'), { hint: t('eml.replyTo.hint'), type: 'email', ltr: true })}
          {text('fromNameEn', t('eml.fromNameEn'), { hint: t('eml.fromName.hint') })}
          <Field id={FIELD_IDS.fromNameAr} label={t('eml.fromNameAr')} error={fe.fromNameAr}>
            {(p) => <input {...p} dir="rtl" lang="ar" autoComplete="off" value={v.fromNameAr} onChange={(e) => setV((x) => ({ ...x, fromNameAr: e.target.value }))} />}
          </Field>
        </div>
      </fieldset>
      {showSmtp ? (
        <fieldset className="subsection">
          <legend>{t('eml.section.smtp')}</legend>
          <div className="formgrid">
            {text('smtpHost', t('eml.smtpHost'), { ltr: true })}
            {text('smtpPort', t('eml.smtpPort'), { type: 'number', ltr: true })}
            <Field id={FIELD_IDS.smtpSecurity} label={t('eml.smtpSecurity')} hint={t('eml.security.hint')} error={fe.smtpSecurity}>
              {(p) => (
                <select {...p} value={security} onChange={(e) => setSecurity(e.target.value as SmtpSecurityDto | '')}>
                  <option value="" />
                  {SMTP_SECURITY_VALUES.map((x) => (
                    <option key={x} value={x}>
                      {tk(`eml.security.${x}`)}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            {text('smtpUsername', t('eml.smtpUsername'), { ltr: true })}
            <div className="field full">
              <span className="tiny">
                {pwState.source === 'environment' ? t('eml.password.state.environment', { name: pwState.environmentName ?? '' }) : tk(`eml.password.state.${pwState.source}`)}{' '}
                {pwState.updatedAt ? t('eml.password.changed', { at: dateTime(pwState.updatedAt) }) : null}
              </span>
            </div>
            <Field id={FIELD_IDS.password} label={t('eml.password.action')} error={fe.password}>
              {(p) => (
                <select {...p} value={pwAction} onChange={(e) => setPwAction(e.target.value as PasswordAction)}>
                  <option value="keep">{t('eml.password.keep')}</option>
                  <option value="set">{t('eml.password.set')}</option>
                  {pwState.source !== 'none' ? <option value="clear">{t('eml.password.clear')}</option> : null}
                  {scope === 'installation' ? <option value="environment">{t('eml.password.environment')}</option> : null}
                </select>
              )}
            </Field>
            {pwAction === 'set' ? (
              <Field id={FIELD_IDS['password.value']} label={t('eml.password.new')} hint={t('eml.password.new.hint')} error={fe['password.value']}>
                {(p) => <input {...p} type="password" dir="ltr" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} />}
              </Field>
            ) : pwAction === 'environment' ? (
              <Field id={FIELD_IDS['password.name']} label={t('eml.password.envName')} hint={t('eml.password.envName.hint', { name: envName })} error={fe['password.name']}>
                {(p) => <input {...p} dir="ltr" autoComplete="off" value={envName} onChange={(e) => setEnvName(e.target.value)} />}
              </Field>
            ) : null}
          </div>
        </fieldset>
      ) : null}
      <p className="tiny">{t('eml.version', { version: initial.version, at: dateTime(initial.updatedAt) })}</p>
      <div className="form-actions">
        <button className="button primary" type="submit" disabled={m.isPending}>
          {m.isPending ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </form>
  );
}

/** "Send a test email" with the server's outcome shown as it is (never "delivered") — PD-586. */
export function TestEmailPanel({ send, lastTest, onDone }: { send: (to: string) => Promise<EmailTestResult>; lastTest: EmailTestStateDto; onDone: () => void }) {
  const { t, tk, dateTime } = useI18n();
  const [to, setTo] = useState('');
  const m = useMutation({ mutationFn: () => send(to.trim()), onSuccess: onDone });
  const fe = m.error instanceof ApiRequestError ? m.error.fields : {};
  const r = m.data;
  const tone = r?.outcome === 'accepted' ? 'green' : r?.outcome === 'unknown' || r?.outcome === 'deferred' ? 'amber' : 'red';
  return (
    <section className="panel" aria-labelledby="eml-test-title">
      <div className="panel-head">
        <div>
          <h2 id="eml-test-title">{t('eml.test.title')}</h2>
          <p>{t('eml.test.intro')}</p>
        </div>
      </div>
      <form
        className="pad"
        noValidate
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <ErrorSummary error={m.error} fieldIds={{ to: 'eml-test-to' }} />
        <div className="formgrid">
          <Field id="eml-test-to" label={t('eml.test.to')} error={fe.to}>
            {(p) => <input {...p} type="email" dir="ltr" autoComplete="email" value={to} onChange={(e) => setTo(e.target.value)} />}
          </Field>
        </div>
        <div className="form-actions">
          <button className="button" type="submit" disabled={m.isPending || !to.trim()}>
            {m.isPending ? t('eml.test.sending') : t('eml.test.send')}
          </button>
        </div>
        {r ? (
          <div className={`note${tone === 'green' ? '' : tone === 'amber' ? ' warning' : ' danger'}`} role="status">
            <strong>
              <span className={`badge ${tone}`}>{tk(`eml.test.outcome.${r.outcome}`)}</span>
            </strong>
            {r.code ? <div>{t('eml.test.code', { code: r.code })}</div> : null}
            {r.detail ? <div>{t('eml.test.detail', { detail: r.detail.startsWith('settings_') ? tk(`eml.problem.${r.detail.slice('settings_'.length)}`, undefined, r.detail) : tk(`eml.detail.${r.detail}`, undefined, r.detail) })}</div> : null}
            <div className="tiny">{t('eml.test.version', { version: r.settingsVersion })}</div>
          </div>
        ) : null}
        <p className="tiny">{t('eml.test.note')}</p>
        <p className="tiny">{lastTest.at && lastTest.outcome ? t('eml.lastTest', { at: dateTime(lastTest.at), outcome: tk(`eml.test.outcome.${lastTest.outcome}`) }) : t('eml.lastTest.none')}</p>
      </form>
    </section>
  );
}

const senderLine = (s: EmailSenderDto) => (s.fromAddress ? `${s.fromNameEn ?? s.fromNameAr ?? ''} <${s.fromAddress}>`.trim() : null);

/** Communications → "Email sender" for organisation administrators (PD-581). */
export function OrgEmailTab() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const key = ['email-settings'];
  const q = useQuery({ queryKey: key, queryFn: () => get<OrganisationEmailSettingsResponse>('/api/v1/email-settings') });
  const refresh = () => void qc.invalidateQueries({ queryKey: key });
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  const d = q.data;
  const inst = senderLine(d.installation.sender);
  return (
    <>
      <section className="panel" aria-labelledby="eml-org-title">
        <div className="panel-head">
          <div>
            <h2 id="eml-org-title">{t('eml.org.title')}</h2>
            <p>{t('eml.org.intro')}</p>
          </div>
          <span className={`badge ${d.inEffect === 'organisation' ? 'green' : ''}`}>{t(d.inEffect === 'organisation' ? 'eml.inEffect.organisation' : 'eml.inEffect.installation')}</span>
        </div>
        <div className="pad">
          <p className="tiny" data-ltr>
            {inst ? t('eml.org.installationSender', { sender: inst }) : t('eml.org.installationSender.none')}
          </p>
          {!d.allowed || !d.override ? (
            <p className="note">{t('eml.org.notAllowed')}</p>
          ) : (
            <>
              <p className={`note${d.override.verifiedCurrent ? '' : ' warning'}`}>{t(d.override.verifiedCurrent ? 'eml.org.verified' : 'eml.org.notVerified')}</p>
              <SenderForm key={d.override.version} scope="organisation" initial={d.override} save={(body) => api('PUT', '/api/v1/email-settings', body)} onSaved={refresh} />
            </>
          )}
        </div>
      </section>
      {d.allowed && d.override ? <TestEmailPanel send={(to) => api<EmailTestResult>('POST', '/api/v1/email-settings/test', { to })} lastTest={d.override.lastTest} onDone={refresh} /> : null}
    </>
  );
}
